import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  CircularProgress,
  Divider,
  Alert,
  Breadcrumbs,
  Link,
  Chip,
  Tooltip,
  Card,
  CardContent
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  AccessTime as AccessTimeIcon,
  Speed as SpeedIcon
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import AuthLayout from '../components/AuthLayout';
import serviceRequestService from '../services/serviceRequest.service';
import clientService, { Client } from '../services/client.service';
import assetService from '../services/asset.service';
import userService from '../services/user.service';
import slaService, { SLA } from '../services/sla.service';
import { useAuth } from '../contexts/AuthContext';
import { ServiceRequestType, ServiceRequestFormValues } from '../types/serviceRequest.types';

interface ClientOption {
  _id: string;
  name: string;
  sla?: string | {
    _id: string;
    name: string;
    responseTime: number;
    resolutionTime: number;
  };
}

interface AssetOption {
  _id: string;
  name: string;
  client: string | {
    _id: string;
    name: string;
  };
}

interface UserOption {
  _id: string;
  displayName: string;
}

const ServiceRequestForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = Boolean(id);
  const { currentUser } = useAuth();
  
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [clients, setClients] = useState<ClientOption[]>([]);
  const [assets, setAssets] = useState<AssetOption[]>([]);
  const [filteredAssets, setFilteredAssets] = useState<AssetOption[]>([]);
  const [technicians, setTechnicians] = useState<UserOption[]>([]);
  const [selectedClient, setSelectedClient] = useState<ClientOption | null>(null);
  const [selectedSLA, setSelectedSLA] = useState<SLA | null>(null);
  const [slaLoading, setSlaLoading] = useState(false);
  
  const validationSchema = Yup.object({
    client: Yup.string().required('Cliente é obrigatório'),
    asset: Yup.string().required('Ativo é obrigatório'),
    type: Yup.string().required('Tipo é obrigatório'),
    description: Yup.string().required('Descrição é obrigatória').min(10, 'Descrição deve ter pelo menos 10 caracteres'),
    priority: Yup.string().required('Prioridade é obrigatória'),
    assignedTo: Yup.string().nullable(),
    estimatedTime: Yup.number().nullable().min(0, 'Tempo estimado deve ser positivo'),
    notes: Yup.string().nullable(),
    'location.address': Yup.string().nullable(),
    'location.latitude': Yup.number().nullable(),
    'location.longitude': Yup.number().nullable()
  });
  
  const formik = useFormik<ServiceRequestFormValues>({
    initialValues: {
      client: '',
      asset: '',
      type: 'maintenance',
      description: '',
      priority: 'medium',
      // @ts-ignore - 'waiting' não é atribuível ao tipo esperado
      status: 'waiting',
      assignedTo: '',
      estimatedTime: undefined,
      notes: '',
      location: {
        address: '',
        latitude: 0,
        longitude: 0
      }
    },
    validationSchema,
    onSubmit: async (values) => {
      setSaveLoading(true);
      setError(null);
      
      try {
        if (isEditing) {
          // @ts-ignore - Incompatibilidade de tipos
          await serviceRequestService.updateServiceRequest(id!, values);
          navigate(`/service-requests/${id}`);
        } else {
          // @ts-ignore - Incompatibilidade de tipos
          const response = await serviceRequestService.createServiceRequest(values);
          navigate(`/service-requests/${response.serviceRequest._id}`);
        }
      } catch (error: any) {
        setError(error.response?.data?.message || 'Erro ao salvar chamado técnico');
      } finally {
        setSaveLoading(false);
      }
    }
  });
  
  useEffect(() => {
    loadFormData();
  }, []);
  
  useEffect(() => {
    // Filter assets based on selected client
    if (formik.values.client) {
      console.log('Filtrando ativos para o cliente:', formik.values.client);
      console.log('Total de ativos disponíveis:', assets.length);
      
      // Filtrar ativos pelo cliente selecionado, considerando diferentes formatos do campo client
      const filtered = assets.filter(asset => {
        // Se asset.client for um objeto, comparar com o ID do objeto
        if (asset.client && typeof asset.client === 'object' && asset.client._id) {
          return asset.client._id === formik.values.client;
        }
        // Se asset.client for uma string, comparar diretamente
        return asset.client === formik.values.client;
      });
      
      console.log('Ativos filtrados:', filtered.length);
      setFilteredAssets(filtered);
      
      // If current selected asset is not in the filtered list, clear it
      if (formik.values.asset && !filtered.some(a => a._id === formik.values.asset)) {
        formik.setFieldValue('asset', '');
      }
      
      // Buscar informações do cliente selecionado
      const client = clients.find(c => c._id === formik.values.client);
      if (client) {
        setSelectedClient(client);
        
        // Se o cliente tem um SLA associado, buscar os detalhes do SLA
        if (client.sla) {
          fetchSLADetails(typeof client.sla === 'object' ? client.sla._id : client.sla);
        } else {
          setSelectedSLA(null);
        }
      } else {
        setSelectedClient(null);
        setSelectedSLA(null);
      }
    } else {
      setFilteredAssets([]);
      formik.setFieldValue('asset', '');
      setSelectedClient(null);
      setSelectedSLA(null);
    }
  }, [formik.values.client, assets, clients]);
  
  // Função para buscar os detalhes do SLA
  const fetchSLADetails = async (slaId: string) => {
    try {
      setSlaLoading(true);
      const slaData = await slaService.getSLAById(slaId);
      setSelectedSLA(slaData);
      
      // Adicionar o SLA ao formulário
      formik.setFieldValue('sla', slaId);
    } catch (error) {
      console.error('Erro ao buscar detalhes do SLA:', error);
      setSelectedSLA(null);
    } finally {
      setSlaLoading(false);
    }
  };
  
  const loadFormData = async () => {
    setLoading(true);
    
    try {
      // Load clients
      const clientsResponse = await clientService.getClients({ limit: 100 });
      // Verificar o formato da resposta do serviço de clientes
      let clientsList: Client[] = [];
      if (clientsResponse.clients) {
        clientsList = clientsResponse.clients;
      // @ts-ignore - Propriedade docs não existe no tipo ClientsResponse
      } else if (clientsResponse.docs) {
        // @ts-ignore - Propriedade docs não existe no tipo ClientsResponse
        clientsList = clientsResponse.docs;
      } else if (Array.isArray(clientsResponse)) {
        clientsList = clientsResponse;
      } else {
        console.error('Formato de resposta inesperado do serviço de clientes:', clientsResponse);
      }
      
      // Filtrar apenas clientes ativos e com SLA associado
      const activeClientsWithSLA = clientsList.filter(client => 
        client.isActive && client.sla
      );
      
      if (activeClientsWithSLA.length === 0) {
        setError('Não há clientes ativos com contrato de SLA. É necessário configurar um SLA para os clientes antes de criar chamados técnicos.');
      }
      
      // Converter para o formato ClientOption
      const clientOptions: ClientOption[] = activeClientsWithSLA.map(client => ({
        _id: client._id || '',
        name: client.name,
        sla: client.sla
      }));
      setClients(clientOptions);
      
      // Load assets
      const assetsResponse = await assetService.getAssets({ limit: 500 });
      // Verificar o formato da resposta do serviço de ativos
      if (assetsResponse.assets) {
        // Converter para o formato AssetOption
        const assetOptions: AssetOption[] = assetsResponse.assets.map(asset => ({
          _id: asset._id || '',
          name: asset.name,
          client: asset.client
        }));
        setAssets(assetOptions);
      // @ts-ignore - Propriedade docs não existe no tipo AssetsResponse
      } else if (assetsResponse.docs) {
        // Converter para o formato AssetOption
        // @ts-ignore - Propriedade docs não existe no tipo AssetsResponse
        const assetOptions: AssetOption[] = assetsResponse.docs.map((asset: any) => ({
          _id: asset._id || '',
          name: asset.name,
          client: asset.client
        }));
        setAssets(assetOptions);
      } else if (Array.isArray(assetsResponse)) {
        setAssets(assetsResponse);
      } else {
        console.error('Formato de resposta inesperado do serviço de ativos:', assetsResponse);
      }
      
      // Load technicians
      try {
        const techniciansResponse = await userService.getAvailableTechnicians();
        if (Array.isArray(techniciansResponse)) {
          setTechnicians(techniciansResponse);
        } else {
          console.error('Formato de resposta inesperado do serviço de técnicos:', techniciansResponse);
        }
      } catch (techError) {
        console.error('Erro ao carregar técnicos:', techError);
        // Continue with the form even if technicians can't be loaded
        setTechnicians([]);
      }
      
      // Load service request data if editing
      if (isEditing) {
        const serviceRequest = await serviceRequestService.getServiceRequestById(id!);
        // @ts-ignore - Incompatibilidade de tipos
        formik.setValues({
          ...serviceRequest,
          // Ensure client and asset are strings, not objects
          // @ts-ignore - Propriedade client não existe no tipo { error: string }
          client: typeof serviceRequest.client === 'object' && serviceRequest.client !== null ? serviceRequest.client._id : serviceRequest.client,
          // @ts-ignore - Propriedade asset não existe no tipo { error: string }
          asset: typeof serviceRequest.asset === 'object' && serviceRequest.asset !== null ? serviceRequest.asset._id : serviceRequest.asset,
          // @ts-ignore - Propriedade assignedTo não existe no tipo { error: string }
          assignedTo: typeof serviceRequest.assignedTo === 'object' && serviceRequest.assignedTo !== null ? serviceRequest.assignedTo._id : serviceRequest.assignedTo || ''
        });
      }
    } catch (err: any) {
      console.error('Erro ao carregar dados:', err);
      setError('Falha ao carregar dados. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout title={isEditing ? 'Editar Chamado Técnico' : 'Novo Chamado Técnico'}>
      <Box sx={{ mb: 4 }}>
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
          <Link 
            color="inherit" 
            href="#" 
            onClick={(e) => { e.preventDefault(); navigate('/service-requests'); }}
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <Typography color="text.primary">Chamados Técnicos</Typography>
          </Link>
          <Typography color="text.primary">
            {isEditing ? 'Editar Chamado Técnico' : 'Novo Chamado Técnico'}
          </Typography>
        </Breadcrumbs>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 500 }}>
            {isEditing ? 'Editar Chamado Técnico' : 'Novo Chamado Técnico'}
          </Typography>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
          >
            Voltar
          </Button>
        </Box>
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Paper elevation={1} sx={{ p: 3 }}>
            <form onSubmit={formik.handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Informações Básicas
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required error={!!formik.touched.client && !!formik.errors.client}>
                    <InputLabel id="client-label">Cliente</InputLabel>
                    <Select
                      labelId="client-label"
                      id="client"
                      name="client"
                      value={formik.values.client}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      label="Cliente"
                    >
                      {clients.length > 0 ? (
                        clients.map((client) => (
                          <MenuItem key={client._id} value={client._id}>
                            {client.name}
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem disabled>Nenhum cliente disponível</MenuItem>
                      )}
                    </Select>
                    {formik.touched.client && formik.errors.client && (
                      <FormHelperText error>{formik.errors.client}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                
                {selectedClient && selectedSLA && (
                  <Grid item xs={12}>
                    <Card variant="outlined" sx={{ mb: 2, bgcolor: '#f5f5f5' }}>
                      <CardContent>
                        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                          Contrato de SLA: {selectedSLA.name}
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                          <Tooltip title="Tempo de resposta">
                            <Chip 
                              icon={<AccessTimeIcon />} 
                              label={`Resposta: ${selectedSLA.responseTime}h`} 
                              color="primary" 
                              variant="outlined" 
                            />
                          </Tooltip>
                          <Tooltip title="Tempo de resolução">
                            <Chip 
                              icon={<SpeedIcon />} 
                              label={`Resolução: ${selectedSLA.resolutionTime}h`} 
                              color="secondary" 
                              variant="outlined" 
                            />
                          </Tooltip>
                        </Box>
                        <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                          * Os tempos são ajustados automaticamente de acordo com a prioridade do chamado
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
                
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required error={!!formik.touched.asset && !!formik.errors.asset}>
                    <InputLabel id="asset-label">Ativo</InputLabel>
                    <Select
                      labelId="asset-label"
                      id="asset"
                      name="asset"
                      value={formik.values.asset}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      label="Ativo"
                      disabled={!formik.values.client}
                    >
                      {filteredAssets && filteredAssets.length > 0 ? (
                        filteredAssets.map((asset) => (
                          <MenuItem key={asset._id} value={asset._id}>
                            {asset.name}
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem disabled>Nenhum ativo disponível</MenuItem>
                      )}
                    </Select>
                    {formik.touched.asset && formik.errors.asset && (
                      <FormHelperText error>{formik.errors.asset}</FormHelperText>
                    )}
                    {!formik.values.client && (
                      <FormHelperText>Selecione um cliente primeiro</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required error={!!formik.touched.type && !!formik.errors.type}>
                    <InputLabel id="type-label">Tipo</InputLabel>
                    <Select
                      labelId="type-label"
                      id="type"
                      name="type"
                      value={formik.values.type}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      label="Tipo"
                    >
                      <MenuItem value="maintenance">Manutenção</MenuItem>
                      <MenuItem value="installation">Instalação</MenuItem>
                      <MenuItem value="inspection">Inspeção</MenuItem>
                      <MenuItem value="repair">Reparo</MenuItem>
                      <MenuItem value="other">Outro</MenuItem>
                    </Select>
                    {formik.touched.type && formik.errors.type && (
                      <FormHelperText error>{formik.errors.type}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required error={!!formik.touched.priority && !!formik.errors.priority}>
                    <InputLabel id="priority-label">Prioridade</InputLabel>
                    <Select
                      labelId="priority-label"
                      id="priority"
                      name="priority"
                      value={formik.values.priority}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      label="Prioridade"
                    >
                      <MenuItem value="low">Baixa</MenuItem>
                      <MenuItem value="medium">Média</MenuItem>
                      <MenuItem value="high">Alta</MenuItem>
                      <MenuItem value="critical">Crítica</MenuItem>
                    </Select>
                    {formik.touched.priority && formik.errors.priority && (
                      <FormHelperText error>{formik.errors.priority}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    multiline
                    rows={4}
                    id="description"
                    name="description"
                    label="Descrição"
                    value={formik.values.description}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.description && Boolean(formik.errors.description)}
                    helperText={formik.touched.description && formik.errors.description}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Localização
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Endereço"
                    name="location.address"
                    value={formik.values.location?.address || ''}
                    onChange={formik.handleChange}
                    // @ts-ignore - Erro de tipagem no formik com propriedades aninhadas
                    error={formik.touched.location?.address && Boolean(formik.errors.location?.address)}
                    // @ts-ignore - Erro de tipagem no formik com propriedades aninhadas
                    helperText={formik.touched.location?.address && formik.errors.location?.address}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Latitude"
                    name="location.latitude"
                    type="number"
                    value={formik.values.location?.latitude || 0}
                    onChange={formik.handleChange}
                    // @ts-ignore - Erro de tipagem no formik com propriedades aninhadas
                    error={formik.touched.location?.latitude && Boolean(formik.errors.location?.latitude)}
                    // @ts-ignore - Erro de tipagem no formik com propriedades aninhadas
                    helperText={formik.touched.location?.latitude && formik.errors.location?.latitude}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Longitude"
                    name="location.longitude"
                    type="number"
                    value={formik.values.location?.longitude || 0}
                    onChange={formik.handleChange}
                    // @ts-ignore - Erro de tipagem no formik com propriedades aninhadas
                    error={formik.touched.location?.longitude && Boolean(formik.errors.location?.longitude)}
                    // @ts-ignore - Erro de tipagem no formik com propriedades aninhadas
                    helperText={formik.touched.location?.longitude && formik.errors.location?.longitude}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Informações Adicionais
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel id="assignedTo-label">Técnico Responsável</InputLabel>
                    <Select
                      labelId="assignedTo-label"
                      id="assignedTo"
                      name="assignedTo"
                      value={formik.values.assignedTo || ''}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      label="Técnico Responsável"
                    >
                      <MenuItem value="">Nenhum</MenuItem>
                      {technicians.length > 0 ? (
                        technicians.map((technician) => (
                          <MenuItem key={technician._id} value={technician._id}>
                            {technician.displayName}
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem disabled>Nenhum técnico disponível</MenuItem>
                      )}
                    </Select>
                    {formik.touched.assignedTo && formik.errors.assignedTo && (
                      <FormHelperText error>{formik.errors.assignedTo}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    id="estimatedTime"
                    name="estimatedTime"
                    label="Tempo Estimado (minutos)"
                    type="number"
                    // @ts-ignore - Propriedade estimatedTime não existe no tipo ServiceRequestType
                    value={formik.values.estimatedTime || ''}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    // @ts-ignore - Propriedade estimatedTime não existe nos tipos FormikTouched e FormikErrors
                    error={formik.touched.estimatedTime && Boolean(formik.errors.estimatedTime)}
                    // @ts-ignore - Propriedade estimatedTime não existe nos tipos FormikTouched e FormikErrors
                    helperText={formik.touched.estimatedTime && formik.errors.estimatedTime}
                    InputProps={{ inputProps: { min: 0 } }}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    id="notes"
                    name="notes"
                    label="Observações"
                    value={formik.values.notes || ''}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.notes && Boolean(formik.errors.notes)}
                    helperText={formik.touched.notes && formik.errors.notes}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <Button
                      variant="outlined"
                      onClick={() => navigate(-1)}
                      sx={{ mr: 2 }}
                    >
                      Cancelar
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={saveLoading}
                      startIcon={saveLoading ? <CircularProgress size={20} /> : <SaveIcon />}
                    >
                      {saveLoading ? 'Salvando...' : 'Salvar'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </form>
          </Paper>
        )}
      </Box>
    </AuthLayout>
  );
};

export default ServiceRequestForm;
